import { Layout } from "@/components/Layout";
import sirfyImg from "@/assets/sirfy.webp";
import gruenerbelImg from "@/assets/gruenerbel.webp";
import simonImg from "@/assets/simon.webp";
import sirfyAvatar from "@/assets/sirfy-avatar.webp";
import gruenerbelAvatar from "@/assets/gruenerbel-avatar.webp";
import simonAvatar from "@/assets/simon-avatar.webp";
import knaufAvatar from "@/assets/dr-knauf.webp";
import knaufImg from "@/assets/knauf-practice.webp";
import heroImg from "@/assets/hero-consultation.jpg.asset.json";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/use-language";

import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ShieldCheck, BarChart3, FileText, Activity, Star, BadgeCheck, Lock, Server, Cpu, FileCheck2, ScrollText } from "lucide-react";
import { fadeUp } from "@/lib/animations";
import { formatNum } from "@/lib/format";
import { ORDER_CTAS_ENABLED } from "@/config/orderCtas";

import trustComplianceImg from "@/assets/trust-compliance.jpg";
import southeastAsiaPilot from "@/assets/praedigo-southeast-asia-pilot.jpg.asset.json";
import { VimeoFacade } from "@/components/VimeoFacade";
import videoPosterDe from "@/assets/video-poster-praedigo-de.jpg.asset.json";
import nassauerLandLogo from "@/assets/praxis-im-nassauer-land.png.asset.json";
import hamburgPhoto from "@/assets/bild_hamburg.jpg.asset.json";


function HeroMockup() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] || "en";
  return (
    <div className="relative w-full max-w-md mx-auto">
      <div className="bg-card rounded-2xl shadow-clinical-xl border border-border p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-destructive" />
            <div className="w-3 h-3 rounded-full bg-warning" />
            <div className="w-3 h-3 rounded-full bg-success" />
          </div>
          <span className="text-xs text-muted-foreground font-medium">Praedigo</span>
        </div>
        <div className="border-t border-border pt-4">
          <p className="text-sm font-semibold text-foreground">{t("hero.mockup.riskAssessment")}</p>
        </div>
        <div className="bg-secondary rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">{t("hero.mockup.ckdRiskScore")}</span>
            <motion.span
              className="text-2xl font-bold text-destructive"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.4, duration: 0.4 }}
            >
              {formatNum("0.82", lang)}
            </motion.span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{t("hero.mockup.riskLevel")}</span>
            <div className="flex items-center gap-2">
              <motion.div
                className="w-4 h-4 rounded-full bg-success"
                initial={{ opacity: 0.15, scale: 0.8 }}
                animate={{
                  opacity: [0.15, 1, 1, 0.3, 0.3],
                  scale: [0.8, 1.2, 1.1, 0.9, 0.9],
                  boxShadow: [
                    "0 0 0px 0px hsl(142 72% 36% / 0)",
                    "0 0 12px 4px hsl(142 72% 36% / 0.5)",
                    "0 0 8px 2px hsl(142 72% 36% / 0.3)",
                    "0 0 0px 0px hsl(142 72% 36% / 0)",
                    "0 0 0px 0px hsl(142 72% 36% / 0)",
                  ],
                }}
                transition={{ duration: 3, times: [0, 0.2, 0.35, 0.5, 1], ease: "easeInOut" }}
              />
              <motion.div
                className="w-4 h-4 rounded-full bg-warning"
                initial={{ opacity: 0.15, scale: 0.8 }}
                animate={{
                  opacity: [0.15, 0.15, 1, 1, 0.3, 0.3],
                  scale: [0.8, 0.8, 1.2, 1.1, 0.9, 0.9],
                  boxShadow: [
                    "0 0 0px 0px hsl(38 92% 50% / 0)",
                    "0 0 0px 0px hsl(38 92% 50% / 0)",
                    "0 0 12px 4px hsl(38 92% 50% / 0.5)",
                    "0 0 8px 2px hsl(38 92% 50% / 0.3)",
                    "0 0 0px 0px hsl(38 92% 50% / 0)",
                    "0 0 0px 0px hsl(38 92% 50% / 0)",
                  ],
                }}
                transition={{ duration: 3, times: [0, 0.3, 0.5, 0.6, 0.75, 1], ease: "easeInOut" }}
              />
              <motion.div
                className="w-5 h-5 rounded-full bg-destructive"
                initial={{ opacity: 0.15, scale: 0.8 }}
                animate={{
                  opacity: [0.15, 0.15, 1, 1],
                  scale: [0.8, 0.8, 1.25, 1.15],
                  boxShadow: [
                    "0 0 0px 0px hsl(0 84% 50% / 0)",
                    "0 0 0px 0px hsl(0 84% 50% / 0)",
                    "0 0 16px 6px hsl(0 84% 50% / 0.6)",
                    "0 0 10px 3px hsl(0 84% 50% / 0.4)",
                  ],
                }}
                transition={{ duration: 3, times: [0, 0.6, 0.8, 1], ease: "easeInOut" }}
              />
            </div>
            <motion.span
              className="text-xs font-bold text-destructive"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.4, duration: 0.4 }}
            >
              {t("hero.mockup.high")}
            </motion.span>
          </div>
          <div className="w-full bg-border rounded-full h-2 overflow-hidden">
            <motion.div
              className="bg-destructive h-2 rounded-full"
              initial={{ width: "0%" }}
              animate={{ width: "82%" }}
              transition={{ delay: 0.5, duration: 2, ease: "easeOut" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}



export default function Index() {
  const { t, i18n } = useTranslation();
  const { localizedPath } = useLanguage();
  const lang = i18n.language?.split("-")[0] || "en";

  return (
    <Layout>
      {/* Hero */}
      <section id="hero" className="relative overflow-hidden py-20 lg:py-28 scroll-mt-16">
        {/* Background image */}
        <img
          src={heroImg.url}
          alt=""
          aria-hidden="true"
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlay for legibility */}
        <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px]" aria-hidden="true" />

        <div className="relative container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-[28px] md:text-[38px] lg:text-[44px] font-medium text-foreground leading-[1.1] mb-3">
              {t("hero.title1")}
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed mb-8 max-w-xl">{t("hero.description")}</p>
            <div className="flex flex-wrap gap-4">
              <Link to={localizedPath("/contact")}>
                <Button
                  style={{ backgroundColor: "#6D84F9" }}
                  className="rounded-full px-6 font-medium text-sm shadow-none text-white hover:opacity-90 transition-opacity"
                >

                  {t("nav.contactUs")}
                </Button>
              </Link>
              <Link to={localizedPath("/product#product-overview")}>
                <Button
                  variant="outline"
                  className="rounded-full px-6 font-medium text-sm border-[#1a1a1a]/15 bg-transparent text-foreground hover:bg-[#1a1a1a]/5"
                >
                  {t("hero.learnMore")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* Logo marquee */}
      <div className="h-px w-full" style={{ backgroundColor: "#8B9DFA" }} />
      <div className="bg-white py-8 overflow-hidden marquee-mask">
        <div className="flex w-max animate-marquee gap-32 pr-32 items-center">
          {(() => {
            const logos: { name: string; logo?: string }[] = [
              { name: "Smartpraxis" },
              { name: "Diabetologie – Fulda Altstadt-Carree" },
              { name: "Praxis Martin Knauf" },
              { name: "Praxis im Nassauer Land", logo: nassauerLandLogo.url },
            ];

            return [...logos, ...logos].map((item, i) =>
              item.logo ? (
                <img
                  key={i}
                  src={item.logo}
                  alt={item.name}
                  title={item.name}
                  className="h-8 md:h-10 w-auto opacity-70"
                />
              ) : (
                <span
                  key={i}
                  className="text-xs md:text-sm font-medium tracking-tight text-muted-foreground/70 whitespace-nowrap"
                >
                  {item.name}
                </span>
              )
            );
          })()}
        </div>
      </div>
      <div className="h-px w-full" style={{ backgroundColor: "#8B9DFA" }} />

      {/* Hamburg screening section */}
      <section id="hamburg" className="py-16 lg:py-24" style={{ backgroundColor: "#FAFAF9" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            className="grid lg:grid-cols-[1.15fr_0.85fr] gap-x-10 gap-y-6 lg:gap-x-16 lg:gap-y-9 items-start"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
          >
            <h2 className="hamburg-heading lg:col-span-2 text-[28px] md:text-[38px] lg:text-[44px] font-medium leading-[1.1]">
              <span className="block">{t("hamburgSection.title1")}</span>
              <span className="block">{t("hamburgSection.title2")}</span>
            </h2>
            <div>
              <p className="text-base text-muted-foreground leading-relaxed mb-4 max-w-xl">
                {t("hamburgSection.body1")}
              </p>
              <p className="text-base text-muted-foreground leading-relaxed max-w-xl">
                {t("hamburgSection.body2")}
              </p>
            </div>
            <div className="overflow-hidden rounded-lg">
              <img
                src={hamburgPhoto.url}
                alt={t("hamburgSection.imageAlt")}
                className="w-full aspect-[3/2] object-cover"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video section — click-to-play (Vimeo facade) */}
      <section id="video" className="py-20 lg:py-28" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <VimeoFacade
              videoId={lang === "de" ? "1231808890" : "1231977638"}
              poster={videoPosterDe.url}
            />
          </div>
        </div>
      </section>

      {/* Clinical Decision Support */}
      <section id="diagnostic-gap" className="py-20 lg:py-28" style={{ backgroundColor: "#FAFAF9" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
          >
            <h2 className="text-[28px] md:text-[38px] lg:text-[44px] font-medium text-foreground leading-[1.1] mb-3">
              {t("clinicalDecision.title")}
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed max-w-xl mx-auto mb-8">
              {t("clinicalDecision.description")}
            </p>

            <div className="flex flex-wrap justify-center gap-3">
              {ORDER_CTAS_ENABLED && (
                <Link to={localizedPath("/bestellung")}>
                  <Button
                    style={{ backgroundColor: "#6D84F9" }}
                    className="rounded-full px-6 font-medium text-sm shadow-none text-white hover:opacity-90 transition-opacity"
                  >
                    {t("clinicalDecision.primaryCta")}
                  </Button>

                </Link>
              )}
              <Link to={localizedPath("/product#product-overview")}>
                <Button
                  variant="outline"
                  className="rounded-full px-6 font-medium text-sm border-[#1a1a1a]/15 bg-transparent text-foreground hover:bg-[#1a1a1a]/5"
                >
                  {t("clinicalDecision.secondaryCta")}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Praedigo helps physicians — picture left, text right */}
      <section id="what-praedigo-does" className="py-20 lg:py-28" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Text aligned left */}
            <div className="order-2 md:order-1 md:text-left text-center">
              <h2 className="text-[28px] md:text-[38px] lg:text-[44px] font-medium text-foreground leading-[1.1] mb-8">
                {t("physiciansHelp.title")}
              </h2>
              <div className="flex flex-wrap md:justify-start justify-center gap-3">
                {ORDER_CTAS_ENABLED && (
                  <Link to={localizedPath("/bestellung")}>
                    <Button
                      style={{ backgroundColor: "#6D84F9" }}
                      className="rounded-full px-6 font-medium text-sm shadow-none text-white hover:opacity-90 transition-opacity"
                    >
                      {t("physiciansHelpCta.bookDemo")}
                    </Button>
                  </Link>
                )}
                <Link to={localizedPath("/product")}>
                  <Button
                    variant="outline"
                    className="rounded-full px-6 font-medium text-sm border-[#1a1a1a]/15 bg-transparent text-foreground hover:bg-[#1a1a1a]/5"
                  >
                    {t("physiciansHelpCta.productDetails")}
                  </Button>
                </Link>
              </div>
            </div>
            {/* Picture aligned right */}
            <motion.div
              className="order-1 md:order-2"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <HeroMockup />
            </motion.div>
          </div>
        </div>
      </section>


      {/* Spacer section */}
      <section className="py-10 lg:py-14" style={{ backgroundColor: "#FFFFFF" }} />










      {/* Physician Testimonials */}
      <section id="references" className="py-20 lg:py-28 scroll-mt-24" style={{ backgroundColor: "#FAFAF9" }}>
        <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-[28px] md:text-[38px] lg:text-[44px] font-medium text-foreground leading-[1.1] mb-3">
              {t("testimonials.title")}
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed max-w-xl mx-auto mb-6">
              {t("testimonials.description")}
            </p>
            <div className="flex justify-center gap-1 text-primary" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={18} fill="currentColor" strokeWidth={0} />
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                quote: t("testimonials.sirfy.quote"),
                name: "Dr. med. Ahmad Sirfy",
                role: t("testimonials.sirfy.role"),
                avatar: sirfyAvatar,
              },
              {
                quote: t("testimonials.simon.quote"),
                name: "Dr. med. Jörg Simon",
                role: t("testimonials.simon.role"),
                avatar: simonAvatar,
              },
              {
                quote: t("testimonials.knauf.quote"),
                name: "Martin Knauf",
                role: t("testimonials.knauf.role"),
                avatar: knaufAvatar,
              },
            ].map((testimonial, i) => (
              <motion.div
                key={testimonial.name}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="bg-card rounded-2xl p-6 border border-border flex flex-col"
              >
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    width="44"
                    height="44"
                    loading="lazy"
                    decoding="async"
                    className="w-11 h-11 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground leading-tight">{testimonial.name}</p>
                    <p className="text-xs text-muted-foreground leading-tight">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">{testimonial.quote}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Southeast Asia pilot — moved from Screening AI Alliance */}
      <section id="taiwan" className="py-20 lg:py-28 scroll-mt-24" style={{ backgroundColor: "#FAFAF9" }}>
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUp}
              className="md:text-left text-center"
            >
              <h2 className="text-[28px] md:text-[38px] lg:text-[44px] font-medium text-foreground leading-[1.1] mb-3">
                {t("accessPage.pilot.title")}
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed">
                {t("accessPage.pilot.desc")}
              </p>
            </motion.div>
            <motion.figure
              className="rounded-2xl overflow-hidden border border-border shadow-clinical"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <img
                src={southeastAsiaPilot.url}
                alt={t("accessPage.pilot.imageCaption")}
                width={1024}
                height={768}
                loading="lazy"
                className="w-full h-auto object-cover"
              />
            </motion.figure>
          </div>
        </div>
      </section>

      {/* Trust and Compliance */}
      <section id="trust" className="py-20 lg:py-28 scroll-mt-24" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-[32px] md:text-[40px] font-medium tracking-tight text-foreground mb-4 leading-tight">
              {t("trust.title")}
            </h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              {t("trust.subtitle")}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div className="space-y-6 order-1 lg:order-1">
              {(t("trust.items", { returnObjects: true }) as { title: string; desc: string }[]).map((item) => (
                <div key={item.title} className="flex gap-3">
                  <ShieldCheck className="shrink-0 mt-0.5 text-primary" size={20} />
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-clinical-lg order-2 lg:order-2">
              <img
                src={trustComplianceImg}
                alt={t("trust.imageAlt")}
                width={1200}
                height={1200}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Certification / standards badges */}
          <div className="mt-16 lg:mt-20 pt-12 border-t border-border/60">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 lg:gap-8">
              {[BadgeCheck, ShieldCheck, Server, Lock, Cpu, FileCheck2]
                .map((icon, idx) => ({
                  icon,
                  ...(t("trust.badges", { returnObjects: true }) as { title: string; desc: string }[])[idx],
                }))
                .map((b) => (
                <div key={b.title} className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-white shadow-clinical flex items-center justify-center mb-3">
                    <b.icon className="text-primary" size={22} />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">{b.title}</h4>
                  <p className="text-xs text-muted-foreground leading-snug">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Final CTA */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
          >
            <h2 className="text-[28px] md:text-[38px] lg:text-[44px] font-medium text-foreground leading-[1.1] mb-3">
              {t("finalCta.title")}
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed max-w-xl mx-auto mb-8">
              {t("finalCta.description")}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to={localizedPath("/contact")}>
                <Button
                  style={{ backgroundColor: "#6D84F9" }}
                  className="rounded-full px-6 font-medium text-sm shadow-none text-white hover:opacity-90 transition-opacity"
                >
                  {t("nav.contactUs")}
                </Button>
              </Link>
              <a href="mailto:info@praedigo.com">
                <Button
                  variant="outline"
                  className="rounded-full px-6 font-medium text-sm border-[#1a1a1a]/15 bg-transparent text-foreground hover:bg-[#1a1a1a]/5"
                >
                  {t("finalCta.emailUs")}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
